import { Exercise } from '../types/exercises';
import { nanoid } from 'nanoid';

export const exerciseLibrary: Exercise[] = [
  // Compound Movements
  {
    id: nanoid(),
    name: "Barbell Back Squat",
    primaryMuscles: ["Quadriceps", "Glutes"],
    secondaryMuscles: ["Hamstrings", "Core", "Lower Back"],
    equipment: ["Barbell", "Squat Rack"],
    category: "strength",
    difficulty: "intermediate",
    force: "push",
    mechanics: "compound",
    instructions: [
      "Position bar on upper back",
      "Feet shoulder width apart",
      "Break at hips and knees",
      "Lower until thighs are parallel",
      "Drive through heels to stand"
    ],
    tips: [
      "Keep chest up",
      "Maintain neutral spine",
      "Drive knees out",
      "Breathe and brace core"
    ],
    image: "https://images.unsplash.com/photo-1566241142559-40e1dab266c6?w=800&q=80"
  },
  {
    id: nanoid(),
    name: "Deadlift",
    primaryMuscles: ["Hamstrings", "Lower Back"],
    secondaryMuscles: ["Glutes", "Core", "Traps"],
    equipment: ["Barbell"],
    category: "strength",
    difficulty: "advanced",
    force: "pull",
    mechanics: "compound",
    instructions: [
      "Stand with feet hip-width apart",
      "Grip bar outside knees",
      "Hinge at hips, flat back",
      "Pull bar up along legs",
      "Lock out hips and knees"
    ],
    tips: [
      "Bar stays close to legs",
      "Chest up throughout",
      "Push floor away",
      "Engage lats before pull"
    ],
    image: "https://images.unsplash.com/photo-1603287681836-b174ce5074c2?w=800&q=80"
  },
  {
    id: nanoid(),
    name: "Bench Press",
    primaryMuscles: ["Chest", "Triceps"],
    secondaryMuscles: ["Front Deltoids", "Core"],
    equipment: ["Barbell", "Bench"],
    category: "strength",
    difficulty: "intermediate",
    force: "push",
    mechanics: "compound",
    instructions: [
      "Lie on bench, feet flat",
      "Grip bar slightly wider than shoulders",
      "Lower bar to chest",
      "Press to starting position",
      "Lock elbows at top"
    ],
    tips: [
      "Retract shoulder blades",
      "Keep wrists straight",
      "Drive with leg drive",
      "Full range of motion"
    ],
    image: "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=800&q=80"
  },

  // Isolation Exercises
  {
    id: nanoid(),
    name: "Dumbbell Bicep Curl",
    primaryMuscles: ["Biceps"],
    secondaryMuscles: ["Forearms"],
    equipment: ["Dumbbells"],
    category: "strength",
    difficulty: "beginner",
    force: "pull",
    mechanics: "isolation",
    instructions: [
      "Stand with dumbbells at sides",
      "Palms facing forward",
      "Curl weights to shoulders",
      "Lower with control",
      "Keep elbows at sides"
    ],
    tips: [
      "Avoid swinging",
      "Full range of motion",
      "Control the negative",
      "Keep core tight"
    ]
  },
  {
    id: nanoid(),
    name: "Tricep Pushdown",
    primaryMuscles: ["Triceps"],
    secondaryMuscles: [],
    equipment: ["Cable Machine"],
    category: "strength",
    difficulty: "beginner",
    force: "push",
    mechanics: "isolation",
    instructions: [
      "Face cable machine",
      "Grip attachment at chest height",
      "Keep elbows at sides",
      "Extend arms down",
      "Control return"
    ],
    tips: [
      "Minimize elbow movement",
      "Squeeze at bottom",
      "Keep shoulders down",
      "Control the weight"
    ]
  },

  // Cardio Exercises
  {
    id: nanoid(),
    name: "Treadmill Sprint",
    primaryMuscles: ["Quadriceps", "Hamstrings"],
    secondaryMuscles: ["Calves", "Core"],
    equipment: ["Treadmill"],
    category: "cardio",
    difficulty: "intermediate",
    instructions: [
      "Start at walking pace",
      "Increase to sprint speed",
      "Maintain form",
      "Recovery walk between intervals"
    ],
    tips: [
      "Land midfoot",
      "Drive arms back",
      "Keep chest up",
      "Control breathing"
    ]
  },
  {
    id: nanoid(),
    name: "Rowing Machine",
    primaryMuscles: ["Back", "Legs"],
    secondaryMuscles: ["Core", "Arms", "Shoulders"],
    equipment: ["Rowing Machine"],
    category: "cardio",
    difficulty: "intermediate",
    instructions: [
      "Sit with feet strapped",
      "Grab handle with overhand grip",
      "Drive with legs",
      "Pull handle to chest",
      "Return to start"
    ],
    tips: [
      "Legs-Core-Arms sequence",
      "Keep back straight",
      "Control the return",
      "Maintain rhythm"
    ]
  },

  // Mobility Exercises
  {
    id: nanoid(),
    name: "Dynamic Hip Stretch",
    primaryMuscles: ["Hip Flexors", "Glutes"],
    secondaryMuscles: ["Lower Back"],
    equipment: [],
    category: "mobility",
    difficulty: "beginner",
    instructions: [
      "Start in lunge position",
      "Drive back knee up",
      "Step back to lunge",
      "Alternate sides"
    ],
    tips: [
      "Keep chest up",
      "Control movement",
      "Breathe steadily",
      "Stay balanced"
    ]
  },
  {
    id: nanoid(),
    name: "Shoulder Dislocates",
    primaryMuscles: ["Shoulders", "Upper Back"],
    secondaryMuscles: ["Chest"],
    equipment: ["Resistance Band"],
    category: "mobility",
    difficulty: "intermediate",
    instructions: [
      "Hold band wide grip",
      "Raise arms overhead",
      "Continue behind back",
      "Return to start"
    ],
    tips: [
      "Keep arms straight",
      "Go slow and controlled",
      "Adjust grip width",
      "Don't force range"
    ]
  },

  // Plyometric Exercises
  {
    id: nanoid(),
    name: "Box Jump",
    primaryMuscles: ["Quadriceps", "Calves"],
    secondaryMuscles: ["Glutes", "Core"],
    equipment: ["Plyo Box"],
    category: "plyometric",
    difficulty: "intermediate",
    instructions: [
      "Stand facing box",
      "Hip hinge countermovement",
      "Explode upward",
      "Land softly on box",
      "Step down to reset"
    ],
    tips: [
      "Land quietly",
      "Use arms for momentum",
      "Start with lower height",
      "Full hip extension"
    ]
  },
  {
    id: nanoid(),
    name: "Medicine Ball Slam",
    primaryMuscles: ["Core", "Shoulders"],
    secondaryMuscles: ["Back", "Arms"],
    equipment: ["Medicine Ball"],
    category: "plyometric",
    difficulty: "intermediate",
    instructions: [
      "Hold ball overhead",
      "Rise onto toes",
      "Slam ball to ground",
      "Catch on bounce",
      "Return to start"
    ],
    tips: [
      "Use hips and core",
      "Full extension",
      "Control the catch",
      "Maintain rhythm"
    ]
  }
];

// Muscle Groups
export const muscleGroups: MuscleGroup[] = [
  {
    id: "legs",
    name: "Legs",
    subgroups: ["Quadriceps", "Hamstrings", "Calves", "Glutes"]
  },
  {
    id: "push",
    name: "Push",
    subgroups: ["Chest", "Shoulders", "Triceps"]
  },
  {
    id: "pull",
    name: "Pull",
    subgroups: ["Back", "Biceps", "Forearms"]
  },
  {
    id: "core",
    name: "Core",
    subgroups: ["Abs", "Obliques", "Lower Back"]
  }
];

// Equipment Categories
export const equipment: Equipment[] = [
  {
    id: "barbell",
    name: "Barbell",
    category: "free weights"
  },
  {
    id: "dumbbell",
    name: "Dumbbells",
    category: "free weights"
  },
  {
    id: "cable",
    name: "Cable Machine",
    category: "machines"
  },
  {
    id: "bodyweight",
    name: "Bodyweight",
    category: "bodyweight"
  },
  {
    id: "bands",
    name: "Resistance Bands",
    category: "accessories"
  }
];